import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import CreateUser from "../../components/Users/CreateUser";
import { UserListingProps } from "../../utils/interface/userListing";
import { getProfileImageExtensionError } from "../../utils/util";
import defaultProfileImage from "../../assets/profile-img.svg";
import { fetchUserById } from "../../api/users/user";
import { useQuery } from "@tanstack/react-query";

type UserListItem = UserListingProps["userList"][0];

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams({ from: "/_protected/user-management/edit/$id" });
  const location = useLocation();
  const userFromState = (location.state as { user?: UserListItem })?.user ?? null;
  const [user, setUser] = useState<UserListItem | null>(userFromState);

  const shouldFetch = Boolean(id) && (!userFromState || userFromState.id !== id);

  const {
    data: fetchedUser,
    isLoading: userLoading,
    isError: userError,
  } = useQuery<UserListItem | null>({
    queryKey: ["users", "by-id", id],
    queryFn: async () => fetchUserById(id as string),
    enabled: shouldFetch,
  });

  useEffect(() => {
    if (!id) return;
    if (userFromState && userFromState.id === id) {
      setUser(userFromState);
      return;
    }
    if (fetchedUser) setUser(fetchedUser);
  }, [id, userFromState, fetchedUser]);

  const notFound = !id || userError || !user || user.id !== id;

  const [profileImageName, setProfileImageName] = useState(
    (user?.profileImagePreview ?? user?.profileImage) ? "Current profile image" : "",
  );
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    (user?.profileImagePreview ?? user?.profileImage) || null,
  );
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImageError, setProfileImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Set profile image when user data is available
  useEffect(() => {
    const displayUrl = user?.profileImagePreview ?? user?.profileImage;
    if (displayUrl) {
      setProfileImagePreview(displayUrl);
      setProfileImageName("Current profile image");
    }
  }, [user]);

  const handleClose = () => navigate({ to: "/user-management" });
  const refreshUsers = () => {};

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const input = event.target;

    if (!file) {
      setProfileImageName("");
      setProfileImagePreview(null);
      setProfileImageFile(null);
      setProfileImageError(null);
      return;
    }

    const extensionError = getProfileImageExtensionError(file);
    if (extensionError) {
      setProfileImageError(extensionError);
      setProfileImageName("");
      setProfileImagePreview(null);
      setProfileImageFile(null);
      if (input) input.value = "";
      return;
    }

    setProfileImageError(null);
    setProfileImageName(file.name);
    setProfileImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  if (userLoading) {
    return (
      <div className="space-y-6">
        <Link
          to="/user-management"
          className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-medium lg:mt-0 mt-[50px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </Link>
        <div className="flex items-center justify-center py-16">
          <div className="w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-6 ">
        <Link
          to="/user-management"
          className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-medium lg:mt-0 mt-[50px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </Link>
        <p className="text-gray-400">
          User not found. Please go back and select a user to edit.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Link
        to="/user-management"
        className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-medium lg:mt-0 mt-[50px]"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Users
      </Link>

      <div>
        <h1 className="text-[34px] lg:text-[38px] xl:text-[56px] font-display font-light text-white mb-4">
          Edit User
        </h1>
        <p className="text-white max-w-2xl font-light text-[14px] lg:text-[16px] xl:text-[18px]">
          Update user details and profile image.
        </p>
      </div>

      <div className="flex flex-col-reverse xl:grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mt-[50px]">
        <div className="xl:max-w-[548px] space-y-4">
          <CreateUser
            initialData={user ?? undefined}
            isEditMode={true}
            refershUsers={refreshUsers}
            setIsModalOpen={(open) => {
              if (!open) handleClose();
            }}
            submitButtonText="UPDATE"
            profileImage={profileImagePreview}
            profileImageFile={profileImageFile}
          />
        </div>

        <div className="flex flex-col">
          <label className="block text-base text-white mb-6">
            Profile image
          </label>
          <div className="flex flex-col items-start gap-[30px]">
            <div className="w-[218px] h-[218px] bg-black rounded-full border border-[#E761A0] flex items-center justify-center overflow-hidden shrink-0">
              <img
                src={(profileImagePreview ?? defaultProfileImage) || undefined}
                alt="Profile"
                className={`${profileImagePreview ? "w-full h-full" : "w-[101px] h-[102px]"} bg-black object-cover rounded-full`}
              />
            </div>
            <div className="flex w-full max-w-[549px] h-[53px] rounded-full border border-[#868686] overflow-hidden">
              <button
                type="button"
                onClick={handleFileButtonClick}
                className="browse-file-btn shrink-0 px-9 h-full bg-white text-gray-800 text-base rounded-full font-semibold uppercase tracking-wide hover:bg-gray-100 transition-colors"
              >
                BROWSE FILE
              </button>
              <input
                type="text"
                readOnly
                placeholder="No file chosen"
                value={profileImageName}
                className="flex-1 min-w-0 h-full px-4 bg-transparent text-gray-400 text-sm placeholder-gray-500 focus:outline-none border-0"
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleFileChange}
            />
            {profileImageError && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                {profileImageError}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
